/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import { type Capacity, CAPACITY_UNIT, Consts, I18nService, PermissionService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { blockSizeE, CapacityPolicyE, healthStatusE, runningStatusE, wormTypeE } from 'app/common/constants';
import { LicenseKey } from 'app/common/constants/license-key';
import { ToolService } from 'app/common/services';
import { ProductConfig } from 'app/config';
import { FeatureConfig } from 'app/config/feature';

import { UnixConfigCardEntryTypeE, UnixPermissionE } from './create-file-system/unix-permisssion-config-card/unix-permisssion-config-card.enum';


export interface ShareResultParams {
    NFS: boolean;
    CIFS: boolean;
}
export interface CommonAntivirusParams {
    switch: boolean;
    fsName: string;
    vstoreId: string;
    vstoreName: string;
}
export enum ActionE {
    Create = 'create',
    Modify = 'modify'
}
export enum ApplicationScrenarioE {
    DataBase = '1',
    VM = '2',
    Custom = '3'
}

export enum RepType {
    HyperReplication = '2',
    HyperMetro = '1'
}

export const auditLogFs = {
    is: {
        value: 'true',
        i18n: 'common.term_yes_label',
    },
    not: {
        value: 'false',
        i18n: 'common.term_No_label',
    },
};

export enum SecurityModeE {
    UNIX = '3',
    NTFS = '2',
    Native = '1',
    Mixed = '0'
}

export enum NasLockingPolicyModeE {
    Mandatory = '0',
    Advisory = '1',
}

export enum PoolVersionE {
    Old = '0',
    New = '1',
    // 二级存储pool新增类型
    FsNew = '2',
    /** vdp容量优化改动后新增的版本号 */
    VdpCapacityOptimization = '3'

}
export const PoolOldVersions = [
    PoolVersionE.Old,
];


export enum AuditItemsE {
    SetSafetyProperty = 0,
    GetSafetyProperty = 1,
    SetProperty = 2,
    GetProperty = 3,
    SetExpandProperty = 4,
    GetExpandProperty = 5,
    Open = 6,
    Close = 7,
    Read = 8,
    Write = 9,
    Create = 10,
    Delete = 11,
    Rename = 12,
    ListFolder = 13
}

export enum AuditLogItemStatusE {
    Invalid = 'invalid',
    Valid = 'valid',
    Unconfigured = 'unconfigured'
}

export enum VaaiSwitchE {
    OFF = '0',
    ON = '1'
}

// 文件共享文件系统创建方式
export enum CreateFileShareTypeE {
    // 新创建
    NEW = 'new',
    // 已存在的文件系统
    EXIST = 'exist'
}

// 由哪个入口进入创建文件共享
export enum ShareEntryE {
    // NFS
    NFS = 'nfs',
    // CIFS
    CIFS = 'cifs',
    // 智能助手
    SmartAssistant = 'SmartAssistant',
}

/**
 * 创建LUN组任务名称替换
 */
export enum FileTaskNameE {
    /** 创建文件系统任务极简下体现创建文件共享任务 */
    createFileShare = 'createFileShare'
}

@Injectable({ providedIn: 'any' })
export class FileSystemStrService {
    get = this.i18nService.get;

    // 容量阈值
    capacityThresholdMin = 50;
    capacityThresholdMax = 99;

    // 快照空间比例
    snapshotMin = 0;
    snapshotMax = 90;

    /**
     * 4294967295表示全选
     */
    AUDIT_ITEMS_ALL_VALUE = 4294967295;

    showSecurityBottomTipItem = [SecurityModeE.UNIX, SecurityModeE.Native, SecurityModeE.Mixed];
    i18n = {
        securityStyleAllTip: this.get('service.filesystem_task_create_securityStyle_all_tip_note'),
        innerFSTip: this.get('service.filesystem_task_interFS_err_tip'),
        hashFSTip: this.get('service.filesystem_task_hashFS_err_tip'),
        snapCompare: this.get('service.filesystem_term_hypersnap_compare'),
        snapCompareTip: this.get('service.filesystem_term_hypersnap_compare_tip'),
        antivirusTitle: this.get('settings.Antivirus_task_title'),
        createBtn: this.get('common.create_button'),
        createAsBtn: this.get('common.createSchema_btn'),
        deleteBtn: this.get('common.delete_button'),
        modifyBtn: this.get('common.modify_button'),
        modifyCapacityBtn: this.get('common.modifyCapacity_button'),
        createSnapshotBtn: this.get('protection.protectionGroup_createSnapshot_button'),
        createCdpBtn: this.get('storage.lun_task_createHyperCDP_button'),
        addToCdpPlan: this.get('storage.lun_task_addtoHyperCDPSchedule_button'),
        createReplicationBtn: this.get('protection.protectionGroup_createReplication_button'),
        createReplicationTip: this.get('protection.dataProtection_term_create_FSRepRemotePair_buttonTips'),
        createNFS: this.get('service.share_term_createNFS_title'),
        createCIFS: this.get('service.share_term_createCIFS_title'),
        createDataTurbo: this.get('service.share_term_createDataTurbo_title'),
        createQuota: this.get('service.quota_term_create_createQuota_tittle'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        closeBtn: this.get('common.close_button'),
        createCloneBtn: this.get('protection.clone_task_createClone_button'),
        createHyperMetroBtn: this.get('protection.protectionGroup_task_createHyperMetro_button'),
        enableAuditLogBtn: this.get('service.fileSystem_term_create_EnableAuditLog_info2'),

        nameLab: this.get('common.name_column'),
        idLab: this.get('common.ID_column'),
        vStoreLab: this.get('common.owningVstore_lab'),
        healthStatusLab: this.get('common.healthStatus_column'),
        runningStatusLab: this.get('common.runningStatus_column'),
        capacityLab: this.get('common.capacity_column'),
        protectionInfoLab: this.get('common.dataProtection_title'),
        storagePoolLab: this.get('storage.LunGroup_term_belongedStoragePool_label'),
        cheduleIdLab: this.get('service.filesystem_term_view_HyperCDPScheduleID_lab'),
        cdpScheduleLab: this.get('storage.lun_task_addToHyperCDPScheduley_HyperCDPSchedule_label'),
        protectionLab: this.get('common.protection_title'),
        protectLabel: this.get('storage.lunGroup_term_protect_button'),
        shareLab: this.get('common.share_lab'),
        dtreeLab: this.get('common.dtree_lab'),
        dtreeNumLab: this.get('service.fileSystem_term_view_dtreeNum_col'),
        quotaLab: this.get('common.quota_lab'),
        quotas: this.get('common.quota_title'),
        basicInfoLab: this.get('common.basicInfo_title'),
        capAndTunningLab: this.get('service.fileSystem_term_capAndTunning_lab'),
        hyperMetroPairLab: this.get('protection.fs_hyperMetro_task_create_pair_lab'),
        remoteVstoreLab: this.get('protection.vstore_hyperMetro_task_create_remoteVstore_lab'),
        fsPropertyLab: this.get('service.fileSystem_term_fsProperty_lab'),
        descriptionLab: this.get('common.description_column'),
        snapshotSpaceRatio: this.get('service.filesystem_term_snapSpaceRatio_lab'),
        share: this.get('common.EnumDefine_MOTYPE_SHARE'),
        maxFileNumLab: this.get('service.filesystem_task_maxFileNum_col'),
        usedFileNumLab: this.get('service.filesystem_task_usedFileNum_col'),
        fileShareTitle: this.get('service.share_simplifiedMode_tab'),
        nfsShareLab: this.get('common.nfsShare_lab'),
        cifsShareLab: this.get('common.cifsShare_lab'),
        dataTurboShareLab: this.get('common.DataTurboShare_lab'),
        nfsShareLowercaseLab: this.get('monitor.charts_term_objectType_NFSShare_icon_label'),
        cifsShareLowercaseLab: this.get('monitor.charts_term_objectType_CIFSshare_icon_label'),
        dataTurboShareLowercaseLab: this.get('common.DataTurboShare_icon_lab'),
        cifs: this.get('common.CIFS_lab'),
        nfs: this.get('common.NFS_lab'),
        comma: this.get('common.comma_label'),
        capacityThresholdLab: this.get('service.filesystem_term_consumedCapThreshold_lab'),
        capacityThresholdWithUnitLab: this.get('service.filesystem_term_consumedCapThreshold_lab') +
            this.get('common.common_unit_lab', ['%']),
        capacityThresholdNumberRange: this.get('common.valueRange_label', [this.capacityThresholdMin, this.capacityThresholdMax]),
        applicationScrenarioLab: this.get('service.filesystem_term_applicationScrenario_lab'),
        blockSizeLab: this.get('service.filesystem_term_blockSize_lab'),
        securityStyleLab: this.get('service.filesystem_term_securityStyle_lab'),
        vaaiLab: this.get('service.filesystem_term_VAAI_lab'),
        lockPolicyLab: this.get('service.fileSystem_task_create_lockPolicy_lab'),
        // 配额开关
        quotaSwitchLab: this.get('service.fileSystem_term_modify_quotaSwitch_lab'),
        // 配额状态
        quotaStatusLab: this.get('service.fileSystem_term_view_quotaStatus_col'),
        //数据缩减开关
        dataReductionSwitchLab: this.get('storage.dataReduction_lab'),
        // 启用
        enable: this.get('common.enable_label'),
        qosPolicyLab: this.get('service.filesystem_term_qosPolicy_lab'),
        isShowSnapshotDirLab: this.get('service.filesystem_term_isShowSnapshotDir_lab'),
        atimeLab: this.get('service.filesystem_term_autoUpdateAtime_lab'),
        atimeUpdateModeLab: this.get('service.filesystem_term_autoUpdateAtime_updateFrequent_lab'),
        summaryLab: this.get('common.task_collectInfo_title_label'),
        protectLab: this.get('common.protection_title'),
        enabledLab: this.get('common.service_enable_value'),
        disabledLab: this.get('common.service_disable_value'),
        enableSwitchLab: this.get('common.enabled_switch_column'),
        disableSwitchLab: this.get('common.disabled_switch_column'),
        totalCapacityLab: this.get('common.totalCapacity_lab'),
        avaliableCapacityLab: this.get('common.availableCapacity_lab'),
        allocCapacityLab: this.get('storage.LunGroup_cap_term_allocatedCapacity_lab'),
        shortAlloCapacityLab: this.get('common.allocatedCapacity_short_lab'),
        usedCapacityRatioLab: this.get('service.fileSystem_term_usedCapaRetio_tab'),
        usedCapacityLab: this.get('common.allocatedCapacity_lab'),
        dataProtectionLab: this.get('storage.storagePool_term_dataProtection_column'),
        operationLab: this.get('common.operation_button'),
        snapshotLab: this.get('common.snapshot_title'),
        cloneLab: this.get('common.clone_button'),
        hyperCDPLab: this.get('protection.HyperCDP_title'),
        replicationLab: this.get('common.replication_title'),
        hyperMetroLab: this.get('common.HyperMetro_title'),
        quotaConfiguredLab: this.get('service.filesystem_term_quotaConfigured_lab'),
        capacityTip: this.get('common.capacity_normalstatu_msg'),
        createCapacityTip: this.get('common.capacity_normalstatu_msg'),
        applicationScenarioTip: this.get('service.filesystem_create_task_applicationScenario_tip'),
        filesystemBlockSizeTip: this.get('service.filesystem_create_task_filesystemBlockSize_tip'),
        advancedLab: this.get('common.advance_label'),
        workloadTypeLab: this.get('storage.lun_term_appType_label'),
        simpleModeWorkloadTypeLab: this.get('service.share_simplifiedMode_create_fsAppType_desc'),
        remoteStoragePollLab: this.get('protection.fs_hyperMetro_task_create_remotePool_lab'),
        parentFsNameLab: this.get('protection.fs_hyperClone_task_create_fatherFS_lab'),
        parentSnapNameLab: this.get('protection.fs_hyperClone_task_view_fatherFShyperSnap_column'),
        isCloneFsLab: this.get('protection.fs_hyperclone_task_view_iscloneFS_col'),
        fsProtectionLab: this.get('common.protection_title'),
        hyperCdpScheduleLab: this.get('protection.HyperCDPSchedule_term_title'),
        hyperCdpScheduleForFileSystemLab: this.get('protection.HyperCDPCG_term_timing_schedule_column'),
        fsHyperMetroDomain: this.get('protection.vstore_hyperMetroDomain_file_lab'),
        hyperMetroDomainLab: this.get('protection.vstore_aFileHyperMetroDomain_lab'),
        noDefaultCdpScheduleTip: this.get('service.fileSystem_task_bulitinHyperCDPSchedule_desc'),
        auditLogLab: this.get('service.fileSystem_term_create_EnableAuditLogValue_lab'),
        preiodLab: this.get('common.Punctuation_preiod'),
        auditLogdisabledTipLab: this.get('service.fileSystem_term_create_EnableAuditLog_info1'),
        switchStandardTip: this.i18nService.get('home.performance_term_enable_switchStandard_tip'),
        auditLogLabTip: this.get('service.fileSystem_term_create_EnableAuditLog_info'),
        notSupportDataTurboEnabled: this.get('service.fileSystem_term_create_dataTurboNotSupportAuditLog_withoutspace_info'),
        notSupportDataTurboUnenabled: this.get('service.fileSystem_term_create_dataTurboNotSupportAuditLog_withspace_info'),
        unconfiguredLab: this.get('common.unconfigured_lab'),
        validLab: this.get('protection.configuration_remoteDevice_task_modify_compressStatus_valid_value'),
        invalidLab: this.get('protection.configuration_remoteDevice_task_modify_compressStatus_invalid_value'),
        totalItemsLab: (value): string => this.get('service.fileSystem_term_view_totleItems_value', [value]),
        configuredInfo: this.get('service.fileSystem_term_view_configItems_info'),
        containerStorageLab: this.get('storage.containerStorage_task_title'),

        // 自定义配额
        quota: this.get('service.quota_term_userQuotas_tittle'),
        // 配额报表
        quotaReport: this.get('service.quota_term_quotaReports_tittle'),
        diffVstoreSelectTip: this.get('storage.lun_task_addToHyperCDPSchedule_tip'),
        createHyperMetroTips: this.get('protection.fs_task_createHyperMetroPair_err_tip'),
        /**
         * 应用请求大小:
         */
        requestSizeInfoLabWithColon: this.get('storage.lun_task_create_appRequestSize_label', true),
        requestSizetVariable: this.get('service.fileSystem_task_create_variableLenFragment_label', true),
        smartDedupLab: this.get('storage.lun_task_create_smartDedupe_label', true),
        smartCompressionLab: this.get('storage.lun_task_create_smartCompression_label', true),
        smartDedupPropertyLab: this.get('storage.lun_task_create_smartDedupe_label'),
        smartCompressionPropertyLab: this.get('storage.lun_task_create_smartCompression_label'),

        distributionAlgoKey: this.get('service.fileSystem_task_create_distributionAlgo_label'),
        distributionAlgoLabel: this.get('service.fileSystem_task_create_distributionAlgo_label', true),
        distributionAlgoPerformanceValue: this.get('service.fileSystem_task_create_distributionAlgo_performance_value'),
        distributionAlgoCapacityValue: this.get('service.fileSystem_task_create_distributionAlgo_capacity_value'),
        /**
         * 应用请求大小
         */
        requestSizeInfoLab: this.get('storage.lun_task_create_appRequestSize_label'),
        minCapacityLimitLab: this.get('service.fileSystem_task_modifyFileSystemCap_minCap_label'),
        maxCapacityLimitLab: this.get('service.fileSystem_task_modifyFileSystemCap_maxCap_label'),
        statusLab: this.get('common.status_column', true),
        strategyLab: this.get('protection.HyperCDPSchedule_term_executePolicy_cloum', true),

        createTitle: this.get('service.fileSystem_term_create_title'),
        copyTitle: this.get('service.fileSystem_term_CreateFromTemp_title'),
        modifyTitle: this.get('service.fileSystem_term_modify_title'),
        modifyCapacityTitle: this.get('service.fileSystem_term_modifyCapa_title'),
        deleteTitle: this.get('service.fileSystem_term_delete_title'),
        modifyCloneTitle: this.get('protection.fs_hyperClone_task_modify_title'),
        addToCdpTitle: this.get('storage.lun_task_addToHyperCDPSchedule_tittle'),

        createMsg: this.get('service.provisioning_task_createFileSystem_info_lessThan3g_msg'),
        deleteMsg: this.get('service.filesystem_delete_task_info_message'),
        SMB3EncryptEnabledTip: this.get('service.share_term_createCIFS_SMB3Encrypt_confirm_msg'),
        enableUnencryptedAccessTip: this.get('service.share_term_changeCIFS_Unencrypted_Access_confirm_msg'),
        wormSwitchExclusiveTip: this.get('service.filesystem_term_worm_info'),
        wormSwitchTipFor2910: this.get('service.filesystem_term_autoUpdateAtime_info'),
        modeNotNoneTip: this.get('service.share_term_changeCIFS_OFFLINEMODE_confirm_msg'),
        enableCaTip: this.get('service.share_term_changeSMB2_CA_ENABLED_confirm_msg'),

        nameFormatErr: this.get('service.fileSystem_term_create_name_valid'),

        check_only_show_share: this.get('service.fileSystem_term_view_fsOwnShares_checkbox'),
        check_only_show_quota: this.get('service.fileSystem_term_view_fsOwnQuotas_checkbox'),
        // 安全模式TIP
        securityTip: this.get(ProductConfig.isOnlySupportUnixSecurityStyle ?
            'service.filesystem_task_create_securityStyle_unix_tip' :
            'service.filesystem_task_create_securityStyle_all_tip'),

        vaaiTip: this.get('service.filesystem_term_VAAI_tip'),
        vaaiMsg: this.get('service.filesystem_term_VAAI_confirm_msg'),
        // 全局重删开关未启用提示
        disableGlobalDedupMsg: this.get('service.filesystem_task_disableGlobalDedup_confirm_msg'),


        /**
         * 审计日志
         */
        auditLog: this.get('settings.auditLog_task_title'),
        enableLab: this.get('common.term_yes_label'),
        disableLab: this.get('common.term_No_label'),
        smartRecommendDataTimeoutMsg: this.get('rest.getSmartRecommendDataTimeout_msg'),
        createAuditLogHighRiskTip: this.get('settings.fileSystem_term_createauditLogFS_confirm_msg'),
        createAuditItemsHighRiskTip: this.get('service.fileSystem_term_create_enableAuditLogValue_comfirm_msg'),

        modifyCapacity: {
            remoteFsblockTitle: this.get('service.fileSystem_term_modifyCapa_remoteFS_lab'),
            usageTip: this.get('service.fileSystem_term_modifyCapa_remoteFS_desc_lab'),
            usageNotAllTip: this.get('service.fileSystem_term_modifyCapa_remoteFS_notAll_desc_lab'),
            tableColumn: {
                remoteDevice: this.get('common.remoteDevice_title'),
                remoteDeviceSn: this.get('common.remoteDeviceSN_column'),
                remoteResource: this.get('service.quota_term_create_FS_lab'),
                remoteResourceId: this.get('common.remoteResourceID_column'),
            },
            rest: (fsName): string => this.get('rest.modifyRemoteFileSystemCapa_msg', [fsName]),
        },

        protectInfo: {
            cdpToolTip: this.get('protection.protection_simplifiedMode_scheduledSnap_tip'),
            tabRadioSnap: this.get('protection.protection_simplifiedMode_OneOffSnap_tab'),
            tabRadioCdp: this.get('protection.protection_simplifiedMode_hyperCDP_tab'),
            snapshot: this.get('common.snapshot_title'),
            cdp: this.get('protection.HyperCDP_title'),
            replication: this.get('common.replication_title'),
            hyperMetro: this.get('common.HyperMetro_title'),
            clone: this.get('protection.fs_hyperClone_task_clones_title'),
            cloneBtn: this.get('common.clone_button'),
        },

        smartCachePartitionLab: this.get('storage.smartCache_task_createPool_smartCachePartition_lab'),
        ownedControllerLab: this.get('service.fileSystem_task_view_ownedController_lab'),
        addFileSystem: (fs, partition): string => this.get('rest.addFStoSmartCachePartition_msg', [fs, partition]),
        dataProtectionCapacity: this.get('storage.storagePool_term_dataProtectionCapacity_column'),
        // 权限数{0},{1}
        permissionsWith: (num: number, str: string): string =>
            this.get('service.share_term_create_permissionNum_value_lab', [num, str]),
        // 所有客户端可读写。
        allClient: this.get('service.share_term_create_permission_allClinetRW_value'),
        // Everyone完全控制
        everyone: this.get('service.share_term_create_permission_everyoneControl_value'),
        // 应用类型-类型
        typeLab: this.get('common.type_column', true),
        // 安全模式修改为UNIX的高危提示
        toUnixContent: this.get('service.filesystem_term_UNIXSecurityStyle_confirm_msg'),
        // 安全模式修改为NTFS的高危提示
        toNtfsContent: this.get('service.filesystem_term_NTPSSecurityStyle_confirm_msg'),
        //安全模式修改为Native或Mixed的高危提示
        toNativeOrMixedContent: this.get('service.filesystem_term_NativeAndMixedSecurityStyle_confirm_msg'),

        // 双活
        remoteStoragePoolError: this.get('common.null_validation', [this.get('protection.fs_hyperMetro_task_create_remotePool_lab')]),
        // 创建时间
        createTime: this.get('common.createTime_label'),
        createHyperMetroTitil: this.get('protection.hyperMetro_task_createPair_title'),
        vstoreNotPairTip: this.get('protection.fs_task_createHyperMetroPair_info_msg'),
        createDataTurboDisabledTip: this.get('service.share_term_createDataTurbo_err_tip'),
        modifyNameMsg: this.get('common.filesystem_task_modifyName_msg'),

        // 极简模式
        overviewLab: this.get('home.AAOverview_term_AAOverview_tittle'),
        simpleDetailTitle: this.get('service.filesystem_simplifiedMode_detail_title'),

        // worm
        worm: {
            title: this.get('service.filesystem_task_create_worm_tab'),
            litigName: this.get('service.filesystem_task_litig_title'),
            compliance: this.get('service.filesystem_task_create_worm_para_mode_value_lawMode_label'),
            enterprise: this.get('service.filesystem_task_create_worm_para_mode_value_enterpriseMode_label'),
            wormType: this.get('service.filesystem_task_create_worm_para_mode_label'),
            wormTypeTips: this.get('service.filesystem_create_task_worm_para_lawMode_meaning_tip'),
            wormTypeEnterpriseTips: this.get('service.filesystem_create_task_worm_para_enterpriseMode_info'),
            retentionLab: this.get('service.filesystem_task_create_worm_para_protectTime_label'),
            retentionTips: this.get('service.filesystem_task_create_worm_para_protectTime_meaning_tip'),
            minRetentionLab: this.get('service.filesystem_task_create_worm_para_protectTime_value_min_label'),
            // 最小保护期范围为0年到70年'
            minRetentionTip: this.get('service.filesystem_task_create_worm_para_lockTime_valid',
                [this.get('service.filesystem_task_create_worm_para_protectTime_value_min_label'), 0, 70],
            ),
            maxRetentionLab: this.get('service.filesystem_task_create_worm_para_protectTime_value_max_label'),
            // 最大保护期不超过70年，且不能为0
            maxRetentionTip: this.get('service.filesystem_task_create_worm_para_lockTime_valid',
                [this.get('service.filesystem_task_create_worm_para_protectTime_value_max_label'), 1, 70],
            ),
            defRetentionLab: this.get('service.filesystem_task_create_worm_para_protectTime_value_default_label'),
            defRetentionTips: this.get('service.filesystem_task_create_worm_para_lockTime_valid',
                [this.get('service.filesystem_task_create_worm_para_protectTime_value_default_label'), 0, 70],
            ),
            infiniteEnableLab: this.get('service.filesystem_task_create_worm_para_protectTime_value_withoutday_checkbox'),
            autoLockLab: this.get('service.filesystem_task_create_worm_para_autoLock_lab'),
            autoLockEnableTips: this.get('service.filesystem_task_create_worm_para_autoLockEnable_info'),
            // 自动锁定的时间范围为1分钟到10年
            autoLockTips: this.get('service.filesystem_task_create_worm_para_lockTime_valid',
                [this.get('service.filesystem_task_create_worm_para_lockTime_label'), 1, 10],
            ),
            enableLab: this.get('common.enable_label'),
            disableLab: this.get('common.disable_label'),
            enableSwitchLab: this.get('common.enabled_switch_column'),
            disableSwitchLab: this.get('common.disabled_switch_column'),
            lockWaiteTimeLab: this.get('service.filesystem_task_create_worm_para_lockTime_label'),
            lockWaiteTimeTips: this.get('service.filesystem_task_create_worm_para_lockTime_meaning_label'),
            autoDeleteLab: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
            autoDeleteTips: this.get('service.filesystem_create_task_worm_para_autoDelete_info'),
            autoDelContent: this.get('service.filesystem_create_task_create_createWorm_info_autoDeletion_msg'),
            wormLogLab: this.get('service.filesystem_task_create_worm_para_auditFs_lab'),
            wormLogContent: this.get('service.filesystem_task_enableWormFs_confirm_msg'),
            wormLogNotContent: this.get('service.filesystem_task_disableWormFs_confirm_msg'),
            initClocks: this.get('service.filesystem_create_task_worm_initializeClock_lab'),
            correntTimeLab: this.get('service.filesystem_create_task_worm_systemTime_lab', [], true),
            initClocksTip: this.get('service.filesystem_create_task_worm_initializeClock_info'),
            initClocksContent: this.get('service.filesystem_task_initializeClock_confirm_msg'),
            timeRangeTips: this.get('service.filesystem_task_create_worm_para_protectTime_validation'),
            createTaskBtn: this.get('service.filesystem_task_createLitigTask_btn'),
            createTaskTitle: this.get('service.filesystem_task_createLitigTask_btn'),
            addTaskBtn: this.get('service.filesystem_task_addLitig_title'),
            delTaskBtn: this.get('service.filesystem_task_removeLitig_title'),
            taskActionLab: this.get('common.operation_button'),
            taskActionTips: this.get('service.filesystem_task_operations_selectOne_info'),
            litigationNameLab: this.get('service.filesystem_task_enableLitigTask_litigName_lab'),
            pathLab: this.get('service.dtree_task_create_fullPath_lab'),
            nameLab: this.get('common.name_column'),
            nameFormatErr: this.get('service.fileSystem_term_create_name_valid'),
            expirationTime: this.get('service.filesystem_view_task_worm_invalidTime_lab'),
            wormLock: this.get('service.filesystem_create_task_worm_clock_lab'),
            fileNum: this.get('service.filesystem_view_task_enableLitigFileNum_lab'),
            yearLab: this.get('common.time_years_label'),
            monthLab: this.get('common.time_months_label'),
            dayLab: this.get('common.lower_days_label'),
            hoursLab: this.get('common.timeAcount_hours_label'),
            minutesLab: this.get('common.timeAcount_minutes_label'),
            advanceLab: this.get('common.advance_label'),
            pathErr: this.get('common.Path_valid', [this.get('service.dtree_task_create_fullPath_lab')]),
            taskName: this.get('service.filesystem_task_litig_title'),
            taskId: this.get('service.filesystem_task_litig_taskID_col'),
            taskOperation: this.get('service.filesystem_task_litig_operation_col'),
            taskStatus: this.get('common.status_column'),
            taskDetailStatus: this.get('service.filesystem_task_litig_statusDetail_col'),
            taskPath: this.get('service.dtree_task_create_fullPath_lab'),
            litigationName: this.get('service.filesystem_task_enableLitigTask_litigName_lab'),
            taskLitingDealOkFileNumber: this.get('service.filesystem_task_litig_dealOkFileNum_col'),
            taskLitingDealFailFileNumber: this.get('service.filesystem_task_litig_dealFailFileNum_col'),
            addLiting: this.get('service.filesystem_task_addLitig_title'),
            deleteLiting: this.get('rest.deleteLitigTask_msg'),
            createLiting: this.get('rest.createLitigTask_msg'),
            deleteLitingPromptTitle: this.get('service.filesystem_task_litig_delete_title'),
            deleteLitingPromptContent: this.get('service.legalhold_delete_task_info_message'),
            paraAuditFsInfo: this.get('service.filesystem_task_create_worm_para_auditFs_info'),
        },

        unixPermissions: {
            userLab: this.get('service.dtree_term_permission_user_lab'),
            userGroupLab: this.get('service.dtree_term_permission_userGroup_lab'),
            otherLab: this.get('service.dtree_term_permission_otherUser_lab'),
            userHelpContent: this.get('service.fileSystem_term_create_rootDirect_user_tip'),
            userGroupHelpContent: this.get('service.fileSystem_term_create_rootDirect_userGroup_tip'),
            otherHelpContent: this.get('service.fileSystem_term_create_rootDirect_other_tip'),
            fsUnixPermissionTitle: this.get('service.fileSystem_term_create_rootDirectPression_lab'),
            dtreeUnixPermissionTitle: this.get('service.dtree_term_create_directPression_lab'),
        },

        // 快照空间比例（%）
        snapshotRate: this.get('service.filesystem_term_snapSpaceRatio_lab'),
        snapshotRateNumberRange: this.get('common.valueRange_label', [this.snapshotMin, this.snapshotMax]),
        // 快照空间比例
        snapshotRate2: this.get('service.filesystem_term_snapSpaceRatio_noUnit_lab'),
        // 删除旧的只读快照
        delReadonlySnapshot: this.get('service.filesystem_create_task_properities_para_deleteOldSnap_label'),
        // 删除旧的只读快照提示信息
        delReadonlySnapshotTips: this.get('service.filesystem_create_task_properities_para_deleteOldSnap_meaning_label'),
        // 容量自适应策略
        capacityPolicy: this.get('service.filesystem_create_task_setCapacitySelfAdaption_policy_label'),
        // 容量自适应策略提示信息
        capacityPolicyTips: this.get('service.filesystem_create_task_setCapacitySelfAdaption_policy_tip'),
        // 自动扩容触发门限（%）
        capacityExpansion: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoexpandThreshold_label'),
        // 自动扩容触发门限提示信息
        capacityExpansionTips: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoexpandThreshold_tip'),
        // 自动扩容上限
        capacityExpansionLimit: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoexpandUpperlimit_label'),
        // 自动扩容上限提示信息
        capacityExpansionLimitTips: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoexpandUpperlimit_tip'),
        // 自动缩容触发门限（%）
        capacityScaleIn: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoreductionThreshold_label'),
        // 自动缩容触发门限（%）提示信息
        capacityScaleInTips: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoreductionThreshold_tip'),
        // 自动缩容下限
        capacityScaleInLimit: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoreductionLowerlimit_label'),
        // 自动缩容下限提示信息
        capacityScaleInLimitTips: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoreductionLowerlimit_tip'),
        // 自动扩（缩）容单次变化量
        autoSingleChange: this.get('service.filesystem_create_task_setCapacitySelfAdaption_singleChangeQuantity_label'),
        // 自动扩缩容门限校验提示
        autoPercentMsg:
            this.get('service.filesystem_create_task_setCapacitySelfAdaption_policy_relationBetweenExpandThresholdAndReductionThreshold'),
        // 自动扩缩容上下限校验提示
        autoSizeMsg: this.get('service.filesystem_create_task_setCapacitySelfAdaption_policy_relationBetweenUpperLimitAndLowerLimit'),
        // 自动扩容默认值规则
        maxAutoSizeMsg: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoexpandUpperlimit_info'),
        // 自动缩容默认值规则
        minAutoSizeMsg: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoreductionLowerlimit_info'),
        // 数据保护预留容量
        snapReserveCapacityLab: this.get('service.filesystem_view_task_general_para_snapReserveCapacity_label'),
        // 数据保护使用容量
        snapUsedCapacityLab: this.get('service.filesystem_view_task_general_para_snapUsedCapacity_label'),
        // 不适用容量自适应
        policyDisableLab: this.get('service.filesystem_create_task_setCapacitySelfAdaption_policy_disable_label'),
        // 自动扩容
        policyAutoExpandLab: this.get('service.filesystem_create_task_setCapacitySelfAdaption_policy_autoExpand_label'),
        // 自动缩容或扩容
        policyAutoExpandorReductionLab:
            this.get('service.filesystem_create_task_setCapacitySelfAdaption_policy_autoExpandorReduction_label'),
        // 自动缩容或扩容
        capacityAndAutoExpandMsg:
            this.get('service.filesystem_create_task_setCapacitySelfAdaption_relationBetweenCapThresholdAndExpandThreshold'),
        // 自动扩容上限必须大于文件系统容量
        capacityGreateAutoExpandMsg: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoexpandUpperlimit_valid'),
        // 自动缩容下限必须小于文件系统容量
        capacityGreateAutoScaleInMsg: this.get('service.filesystem_create_task_setCapacitySelfAdaption_autoreductionLowerlimit_valid'),
        // WORM文件系统不支持启用自动更新Atime。
        wormAndAtimeTips: this.get('service.filesystem_term_autoUpdateAtime_info'),
        // 快照空间比提示信息。
        snapShotTips: this.get('service.filesystem_term_snapSpaceRatio_tip'),

        // 创建文件共享
        noPoolMessageTip1: this.get('storage.lun_task_create_noPool_info1'),
        noPoolMessageTip2: this.get('storage.storagePool_create_task_title_wintitle'),
        noPoolMessageTip3: this.get('service.share_simplifiedMode_create_err_info'),
        recommendedConfigTitle: this.get('service.share_simplifiedMode_create_recommendedConfig_lab'),
        createFileShareTitle: this.get('service.share_simplifiedMode_create_wintitle'),
        selecteCreateTypeTitle: this.get('service.share_simplifiedMode_create_selectWay_title'),
        fileSystem: this.get('service.quota_term_create_FS_lab'),
        fastCreateFileShareTitle: this.get('service.share_simplifiedMode_create_newFS_lab'),
        fastCreateFileShareInfo: this.get('service.share_simplifiedMode_create_newFS_info'),
        selectFileSystemCreateShareTitle: this.get('service.share_simplifiedMode_create_existFS_lab'),
        selectFileSystemCreateShareInfo: this.get('service.share_simplifiedMode_create_existFS_info'),
        filesystemNameTitle: this.get('service.quota_term_FSName_lab'),
        fileSystemCreateShareInfo: (nfsPath: string, cifsPath: string, sharePath: string): string =>
            this.get('service.share_simplifiedMode_create_newName_info', [nfsPath, cifsPath, sharePath]),
        fileSystemCreateShareNfsExistInfo: (cifsPath: string, sharePath: string): string =>
            this.get('service.share_simplifiedMode_create_existNFSname_info', [cifsPath, sharePath]),
        fileSystemCreateShareRootInfo: (nfsPath: string, sharePath: string): string =>
            this.get('service.share_simplifiedMode_create_rootFSNewName_info', [nfsPath, sharePath]),
        createNfsRestMsg: (sharePath: string): string => this.get('rest.CreateNfsShare_msg', [sharePath]),
        settingShareNeedCapacityTitle: this.get('service.share_simplifiedMode_create_capacity_lab'),
        advancedSettingTitle: this.get('service.share_simplifiedMode_create_advance_btn'),
        shareProtocolTitle: this.get('service.share_simplifiedMode_create_shareProtocol_lab'),
        shareProtocolInfo: this.get('service.share_simplifiedMode_create_shareProtocol_info'),
        needOneShareTip: this.get('service.share_simplifiedMode_create_enableShare_info'),
        modifyNameNfsLab: this.get('service.share_simplifiedMode_modifyName_NFS_lab'),
        modifyNameCifsLab: this.get('service.share_simplifiedMode_modifyName_CIFS_lab'),
        clearNfsPermissionLab: this.get('service.share_term_clearNFSPermissions_msg'),
        clearCifsPermissionLab: this.get('service.share_term_clearCIFSPermissions_msg'),
        newShareNameNfsAndCifsInfo: (nfsPath: string, cifsPath: string, sharePath: string): string =>
            this.get('service.share_simplifiedMode_create_newName_NFSCIFS_info1', [nfsPath, cifsPath, sharePath]),
        newShareNameNfsInfo: (nfsPath: string, sharePath: string): string =>
            this.get('service.share_simplifiedMode_create_newName_NFS_info1', [nfsPath, sharePath]),
        newShareNameCifsInfo: (cifsPath: string, sharePath: string): string =>
            this.get('service.share_simplifiedMode_create_newName_CIFS_info1', [cifsPath, sharePath]),
        ifNeedLab: this.get('service.share_simplifiedMode_create_newName_info2'),
        modifyShareNameBtn: this.get('service.share_simplifiedMode_create_newName_info3'),
        modifyShareNameTitle: this.get('service.share_simplifiedMode_modifyShareName_title'),
        configShareLab: this.get('service.share_simplifiedMode_create_newName_info4'),
        nfsShareNameLab: this.get('service.share_simplifiedMode_modifyName_NFS_lab'),
        cifsShareNameLab: this.get('service.share_simplifiedMode_modifyName_CIFS_lab'),
        existNfsShareLab: this.get('service.share_simplifiedMode_create_existNFSname_info1'),
        dataProtectTitle: this.get('common.dataProtection_title'),
        addNewScheduleBtn: this.get('service.share_simplifiedMode_create_addHypercdp_btn'),
        cdpScheduleTitle: this.get('service.share_simplifiedMode_create_hypercdpSnap_lab'),
        createCdpScheduleBtn: this.get('service.share_simplifiedMode_create_addHypercdp_btn'),
        defaultCdpScheduleTitle: this.get('service.share_simplifiedMode_create_defaultHypercdp_lab'),
        storagePoolTitle: this.get('common.storagePool_title'),
        cdpEnableSwitchLabel: this.get('protection.protection_simplifiedMode_ScheduledSnapSwitch_col'),
    };

    /**
     * 根目录权限
     */
    unixPermissionLabel = {
        [UnixPermissionE.READ]: this.get('service.dtree_term_permission_read_lab'),
        [UnixPermissionE.WRITE]: this.get('service.dtree_term_permission_write_lab'),
        [UnixPermissionE.EXECUTE]: this.get('service.dtree_term_permission_execute_lab'),
    };

    /**
     * 根目录权限选项
     */
    unixPermissionOptions: SelectOption[] = [
        {
            label: this.get('service.dtree_term_permission_read_lab'),
            value: UnixPermissionE.READ,
        },
        {
            label: this.get('service.dtree_term_permission_write_lab'),
            value: UnixPermissionE.WRITE,
        },
        {
            label: this.get('service.dtree_term_permission_execute_lab'),
            value: UnixPermissionE.EXECUTE,
        },
    ];

    /**
     * 根目录标题
     */
    unixPermissionTitle = {
        [UnixConfigCardEntryTypeE.FILESYSTEM]: this.get('service.fileSystem_term_create_rootDirectPression_lab'),
        [UnixConfigCardEntryTypeE.DTREE]: this.get('service.dtree_term_create_directPression_lab'),
    };

    /**
     * worm诉讼保留任务状态
     */
    wormLitingStatus = {
        0: this.get('common.EnumDefine_RUNNING_STATUS_E_HyperCopy_INITIALIZING'),
        1: this.get('monitor.task_status_result_running'),
        2: this.get('common.EnumDefine_RUNNING_STATUS_E_JOB_COMPLETED'),
        3: this.get('common.EnumDefine_RUNNING_STATUS_E_ERROR'),
        4: this.get('common.EnumDefine_RUNNING_STATUS_E_HyperCopy_DELETING'),
    };

    /**
     * worm诉讼保留任务状态详情
     */
    wormLitingDetailStatus = {
        0: this.get('service.filesystem_task_litig_taskStatus_noerr_value'),
        1: this.get('service.filesystem_task_litig_taskStatus_InternalError_value'),
        2: this.get('service.filesystem_task_litig_taskStatus_maxFile_value'),
        3: this.get('service.filesystem_task_litig_taskStatus_maxFS_value'),
        4: this.get('service.filesystem_task_litig_taskStatus_poolFault_value'),
        5: this.get('service.filesystem_task_litig_taskStatus_pathOutofRange_value'),
    };

    /**
     * worm诉讼保留任务操作类型下拉选项
     */
    wormLitingOperationOptions = [
        {
            value: 1,
            label: this.get('service.filesystem_task_addLitig_title'),
        },
        {
            value: 0,
            label: this.get('service.filesystem_task_removeLitig_title'),
        },
    ];

    /**
     * worm诉讼保留任务操作类型
     */
    wormLitingOperationType = {
        1: this.get('service.filesystem_task_addLitig_title'),
        0: this.get('service.filesystem_task_removeLitig_title'),
    };

    /**
     * worm诉讼保留任务操作字段
     */
    wormLitingTaskOperationType = {
        1: this.get('rest.addLitig_msg'),
        0: this.get('rest.removeLitig_msg'),
    };

    /**
     * 时间单位
     * 48=年， 47=月， 46=天， 45=小时， 44=分钟
     */
    timeOptions = {
        48: this.i18n.worm.yearLab,
        47: this.i18n.worm.monthLab,
        46: this.i18n.worm.dayLab,
        45: this.i18n.worm.hoursLab,
        44: this.i18n.worm.minutesLab,
    };

    /**
     * worm模式
     */
    wormTypeMap = {
        1: this.i18n.worm.compliance,
        3: this.i18n.worm.enterprise,
    };

    /**
     * "0"=内置，"1"=自定义
     */
    workloadCreateTypeMap = {
        0: this.get('common.buildin_value'),
        1: this.get('common.custom_value'),
    };

    auditItem = {
        [AuditItemsE.SetSafetyProperty]: this.get('service.fileSystem_term_create_EnableAuditLog_SetSafetyProperty_value'),
        [AuditItemsE.GetSafetyProperty]: this.get('service.fileSystem_term_create_EnableAuditLog_GetSafetyProperty_value'),
        [AuditItemsE.SetProperty]: this.get('service.fileSystem_term_create_EnableAuditLog_SetProperty_value'),
        [AuditItemsE.GetProperty]: this.get('service.fileSystem_term_create_EnableAuditLog_GetProperty_value'),
        [AuditItemsE.SetExpandProperty]: this.get('service.fileSystem_term_create_EnableAuditLog_SetExpandProperty_value'),
        [AuditItemsE.GetExpandProperty]: this.get('service.fileSystem_term_create_EnableAuditLog_GetExpandProperty_value'),
        [AuditItemsE.Open]: this.get('service.fileSystem_term_create_EnableAuditLog_Open_value'),
        [AuditItemsE.Close]: this.get('service.fileSystem_term_create_EnableAuditLog_Close_value'),
        [AuditItemsE.Read]: this.get('service.fileSystem_term_create_EnableAuditLog_Read_value'),
        [AuditItemsE.Write]: this.get('service.fileSystem_term_create_EnableAuditLog_Write_value'),
        [AuditItemsE.Create]: this.get('service.fileSystem_term_create_EnableAuditLog_Create_value'),
        [AuditItemsE.Delete]: this.get('service.fileSystem_term_create_EnableAuditLog_Delete_value'),
        [AuditItemsE.Rename]: this.get('service.fileSystem_term_create_EnableAuditLog_Rename_value'),
        [AuditItemsE.ListFolder]: this.get('service.fileSystem_term_create_EnableAuditLog_listFolder_value'),
    };

    fsCapacityMin = '1 GB';
    fsCapacityMax = '32 PB';
    auditLogFsCapacityMin = '5 GB';
    // 下面带小数点的容量值是在修改容量页面显示范围使用
    fsCapacityMinWithDecimal = '1.000 GB';
    fsCapacityMaxWithDecimal = '32.000 PB';

    // 扩容门限
    expansionMin = 2;
    expansionMax = 98;
    errorCount = 2;
    // 缩容门限
    scaleInMin = 1;
    scaleInMax = 97;

    // 自动扩缩容默认值
    autoGrowDefValue = 85;
    autoShrinkDefValue = 50;

    /**
     * 健康状态选项
     */
    healthStatusOpts: SelectOption[] = this.toolService.getKeyValeOptions(healthStatusE, [
        healthStatusE.Normal.value,
        healthStatusE.Fault.value,
        healthStatusE.WriteProtect.value,
        healthStatusE.Invalid.value,
    ]);

    /**
     * 审计日志选项
     */
    auditLogFsOpts: SelectOption[] = this.toolService.getKeyValeOptions(auditLogFs, [
        auditLogFs.is.value, auditLogFs.not.value,
    ]);

    /**
     * 运行状态选项
     */
    runningStatusOpts: SelectOption[] = this.toolService.getKeyValeOptions(runningStatusE, [
        runningStatusE.Online.value,
        runningStatusE.Offline.value,
        runningStatusE.Invalid.value,
        runningStatusE.Initializing.value,
    ]);

    /**
     * 应用场景选项
     */
    applicationScrenarioOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.filesystem_term_applicationScrenario_vm_lab'),
            value: ApplicationScrenarioE.VM,
        },
        {
            label: this.i18nService.get('service.filesystem_term_applicationScrenario_database_lab'),
            value: ApplicationScrenarioE.DataBase,
        },
        {
            label: this.i18nService.get('service.filesystem_term_applicationScrenario_custom_lab'),
            value: ApplicationScrenarioE.Custom,
        },
    ];

    /**
     * 块大小选项
     */
    blockSizeOpts: SelectOption[] = [
        {
            label: '4 KB',
            value: 4 * 1024,
        },
        {
            label: '8 KB',
            value: 8 * 1024,
        },
        {
            label: '16 KB',
            value: 16 * 1024,
        },
        {
            label: '32 KB',
            value: 32 * 1024,
        },
        {
            label: '64 KB',
            value: 64 * 1024,
        },
        {
            label: '> 64 KB',
            value: 128 * 1024,
        },
    ];

    tempSecurityOpts: SelectOption[] = [
        ...(ProductConfig.isOnlySupportUnixSecurityStyle ? [
            {
                label: this.i18nService.get('service.dtree_task_create_securityStyle_UNIX_value'),
                value: SecurityModeE.UNIX,
            },
        ] : [
            {
                label: this.i18nService.get('service.dtree_task_create_securityStyle_UNIX_value'),
                value: SecurityModeE.UNIX,
            },
            {
                label: this.get('service.filesystem_term_securityStyle_ntfs_lab'),
                value: SecurityModeE.NTFS,
            },
            {
                label: this.get('service.filesystem_term_securityStyle_native_lab'),
                value: SecurityModeE.Native,
            },
            {
                label: this.get('service.filesystem_term_securityStyle_mixed_lab'),
                value: SecurityModeE.Mixed,
            },
        ]),
    ];

    /**
     * NAS锁定策略
     */
    nasLockingPolicyOpts: SelectOption[] = [
        {
            label: this.get('service.fileSystem_task_create_lockPolicy_mandatory_value'),
            value: NasLockingPolicyModeE.Mandatory,
        },
        {
            label: this.get('service.fileSystem_task_create_lockPolicy_advisory_value'),
            value: NasLockingPolicyModeE.Advisory,
        },
    ];

    /**
     *  配额开关状态
     *  0：关闭
     *  1：开启
     *  2：正在初始化
     */
    quotaSwitchStatusOpts: SelectOption[] = [
        {
            label: this.i18nService.get('common.disabled_switch_column'),
            value: '0',
        },
        {
            label: this.i18nService.get('common.enabled_switch_column'),
            value: '1',
        },
        {
            label: this.i18nService.get('common.EnumDefine_RUNNING_STATUS_E_INITIALIZING'),
            value: '2',
        },
    ];

    /**
     * 安全模式选项
     */
    securityStyleOpts: SelectOption[] = [
        {
            label: this.get('service.filesystem_term_securityStyle_mixed_lab'),
            value: '0',
        },
        {
            label: this.get('service.filesystem_term_securityStyle_native_lab'),
            value: '1',
        },
        {
            label: this.get('service.filesystem_term_securityStyle_ntfs_lab'),
            value: '2',
        },
        {
            label: this.get('service.filesystem_term_securityStyle_unix_lab'),
            value: '3',
        },
    ];

    /**
     * 快照目录是否可见选项
     */
    showSnapshotDirOpts: SelectOption[] = [
        {
            label: this.get('service.filesystem_term_isShowSnapshotDir_visible_lab'),
            value: true,
        },
        {
            label: this.get('service.filesystem_term_isShowSnapshotDir_unvisible_lab'),
            value: false,
        },
    ];

    /**
     * aTime更新频率选项
     */
    atimeUpdateModeOpts: SelectOption[] = [
        {
            label: this.get('service.filesystem_term_autoUpdateAtime_updateFrequent_hourly_lab'),
            value: 3600,
        },
        {
            label: this.get('service.filesystem_term_autoUpdateAtime_updateFrequent_daily_lab'),
            value: 86400,
        },
    ];

    /**
     * worm模式
     */
    wormTypes: SelectOption[] = [
        {
            label: this.i18n.worm.compliance,
            value: wormTypeE.compliance,
        },
    ];

    DistAlgOpts: SelectOption[] = [
        {
            value: '0',
            label: this.get('service.fileSystem_task_create_distributionAlgo_performance_value'),
        },
        {
            value: '1',
            label: this.get('service.fileSystem_task_create_distributionAlgo_capacity_value'),
        },
        {
            value: '2',
            label: this.get('service.fileSystem_task_create_distributionAlgo_directBalance_value'),
        },
        {
            value: '3',
            label: this.get('service.fileSystem_task_create_distributionAlgo_directShuffle_value'),
        },
        {
            value: '4',
            label: this.get('service.fileSystem_task_create_distributionAlgo_controllerAffinity_value'),
        },
    ];

    isCloneOpts: SelectOption[] = [
        {
            value: 'true',
            label: this.get('common.term_yes_label'),
        },
        {
            value: 'false',
            label: this.get('common.term_No_label'),
        },
    ];

    /**
     * 定时计划状态
     */
    scheduleStatusOpts: SelectOption[] = [
        {
            label: this.get('common.service_enable_value'),
            value: '30',
        },
        {
            label: this.get('common.service_disable_value'),
            value: '31',
        },
        {
            label: this.get('common.EnumDefine_RUNNING_STATUS_E_INITIALIZING'),
            value: '53',
        },
        {
            label: this.get('common.EnumDefine_RUNNING_STATUS_E_REMOVING'),
            value: '106',
        },
        {
            label: this.get('common.EnumDefine_RUNNING_STATUS_E_STOPPING'),
            value: '111',
        },
    ];

    auditItemsOpts: SelectOption[] = [
        {
            label: this.auditItem[AuditItemsE.Create],
            value: AuditItemsE.Create,
        },
        {
            label: this.auditItem[AuditItemsE.Read],
            value: AuditItemsE.Read,
        },
        {
            label: this.auditItem[AuditItemsE.GetSafetyProperty],
            value: AuditItemsE.GetSafetyProperty,
        },
        {
            label: this.auditItem[AuditItemsE.Delete],
            value: AuditItemsE.Delete,
        },
        {
            label: this.auditItem[AuditItemsE.Rename],
            value: AuditItemsE.Rename,
        },
        {
            label: this.auditItem[AuditItemsE.SetSafetyProperty],
            value: AuditItemsE.SetSafetyProperty,
        },
        {
            label: this.auditItem[AuditItemsE.Write],
            value: AuditItemsE.Write,
        },
        {
            label: this.auditItem[AuditItemsE.ListFolder],
            value: AuditItemsE.ListFolder,
        },
        {
            label: this.auditItem[AuditItemsE.GetExpandProperty],
            value: AuditItemsE.GetExpandProperty,
        },
        {
            label: this.auditItem[AuditItemsE.Open],
            value: AuditItemsE.Open,
        },
        {
            label: this.auditItem[AuditItemsE.GetProperty],
            value: AuditItemsE.GetProperty,
        },
        {
            label: this.auditItem[AuditItemsE.SetExpandProperty],
            value: AuditItemsE.SetExpandProperty,
        },
        {
            label: this.auditItem[AuditItemsE.Close],
            value: AuditItemsE.Close,
        },
        {
            label: this.auditItem[AuditItemsE.SetProperty],
            value: AuditItemsE.SetProperty,
        },
    ];

    constructor(
        private i18nService: I18nService,
        private utilService: UtilService,
        private toolService: ToolService,
        private permissionService: PermissionService,
    ) { }

    /**
     * smartgui智能推荐
     */
    smartgui = {
        recDes: this.i18nService.get('common.common_term_SmartRecommend_des_lab'),
    };

    /**
     * 容量范围错误
     * @param min
     * @param max
     */
    capacityRangeErr(min: string, max: string): string {
        return this.toolService.rangeErr(
            this.i18n.capacityLab,
            min,
            max,
        );
    }

    /**
     * 自动扩缩容门限错误
     */
    autoSizeRangeErr(type: 'max' | 'min', isAuditLogFS: boolean): string {
        const name = type === 'max' ? this.i18n.capacityExpansionLimit : this.i18n.capacityScaleInLimit;
        const minLab = isAuditLogFS ? this.auditLogFsCapacityMin : this.fsCapacityMin;
        return this.toolService.rangeErr(
            name,
            minLab,
            this.fsCapacityMax,
        );
    }

    /**
     * 自动缩容下限错误
     */
    minAutoSizeRangeErr(isAuditLogFS: boolean): string {
        const minLimit = isAuditLogFS ? this.auditLogFsCapacityMin : this.fsCapacityMin;
        return this.i18nService.get('service.filesystem_create_task_setCapacitySelfAdaption_autoreductionLowerlimit_valid', [minLimit]);
    }

    /**
     * 是否显示容量偏差提示，不足一个sector
     */
    getLossTip(cap: Capacity, type: 'create' | 'modify' = 'create'): string {
        let capLossTip: string;
        const { value, unit } = cap;

        if (value && (unit !== Consts.SECTOR && unit !== Consts.MB)) {
            const capByteObj = this.utilService.adaptUnit(value, unit, Consts.SECTOR);
            if (Math.floor(capByteObj.value) !== capByteObj.value) {
                if (type === 'create') {
                    capLossTip = this.get('service.fileSystem_task_createFileSystem_desc_vagueValue_label',
                        [value + ' ' + unit, capByteObj.value.toFixed(3)]);
                } else if (type === 'modify') {
                    capLossTip = this.get('service.fileSystem_task_modifyFileSystemCap_desc_vagueValue_label',
                        [value + ' ' + unit, capByteObj.value.toFixed(3)]);
                }
            }
        }

        return capLossTip;
    }

    /**
     * 创建结果
     * @param name
     */
    createResult(name: string): string {
        return this.i18nService.get('rest.createFileSystem_msg', [name]);
    }

    /**
     * 复制结果
     * @param name
     */
    createAsResult(name: string): string {
        return this.i18nService.get('rest.cropyFileSystem_msg', [name]);
    }

    /**
     * 修改结果
     * @param name
     */
    modifyResult(name: string): string {
        return this.i18nService.get('rest.modifyFileSystem_msg', [name]);
    }

    /**
     * 修改克隆结果
     * @param name
     */
    modifyCloneResult(name: string): string {
        return this.i18nService.get('rest.modifyfsHyperClone_msg', [name]);
    }

    /**
     * 修改容量结果
     * @param name
     */
    modifyCapacityResult(name: string): string {
        return this.i18nService.get('rest.modifyFileSystemCapa_msg', [name]);
    }

    /**
     * 删除结果
     * @param name
     */
    deleteResult(name: string): string {
        return this.i18nService.get('rest.deleteFileSystem_msg', [name]);
    }

    /**
     * 创建诉讼保留任务
     * @param name
     */
    createLitingTask(operation: number, name: string): string {
        return this.i18nService.get('rest.createLitigTask_msg', [this.formatLitingTaskOperation(operation), name]);
    }

    /**
     * 删除诉讼保留任务
     * @param name
     */
    deleteLitingTask(operation: number, name: string): string {
        return this.i18nService.get('rest.deleteLitigTask_msg', [this.formatLitingTaskOperation(operation), name]);
    }

    /**
     * 容量使用情况
     */
    capacityUsage = (total: string | number, used: string | number): string => this.get('common.usedandTotal_tip', [
        this.utilService.adaptUnit(used, 'Sector').label,
        this.utilService.adaptUnit(total, 'Sector').label,
    ]);

    /**
     * 范围提示
     */
    rangeTip = (min: number | string, max: number | string): string => this.get('common.valueRange_label', [min, max]);

    /**
     * 获取应用请求大小字符串
     * @param blockSizeNum 应用请求大小代号
     */
    getRequestSizeStrByNum = (blockSizeNum: number): string => this.i18n.requestSizeInfoLabWithColon + blockSizeE[blockSizeNum];

    /**
     * 获取平均分片大小字符串
     * @param blockSizeNum 平均分片大小
     */
    getVariableSegmentSizeStrByNum = (variableSegmentNum: number): string => this.i18n.requestSizetVariable + variableSegmentNum + 'KB';

    /**
     * 获取应用请求大小字符串
     * @param blockSize 文件系统块大小
     */
    getRequestSizeStr = (blockSize: number): string =>
        this.i18n.requestSizeInfoLabWithColon + this.utilService.getLabelFromOptions(this.blockSizeOpts, blockSize);

    /**
     * 获取auditItems下发到底层得参数
     */
    getAuditItemParam = (selections: number[]): number => {
        let param = 0;
        if (selections.length > 0) {
            const auditItemsArr = [];
            Object.keys(this.auditItem).forEach((item) => {
                if (selections.some((selection) => selection === Number(item))) {
                    auditItemsArr.unshift(1);
                } else {
                    auditItemsArr.unshift(0);
                }
            });

            param = parseInt(auditItemsArr.join(''), 2);
        }

        return param;
    };

    /**
     * 获取已选审计项
     */
    getSelectedAuditItem = (num: number): number[] => {
        const selections: number[] = [];
        num.toString(2).split('').reverse().forEach((item, i) => {
            if (Number(item) === 1) {
                selections.push(i);
            }
        });

        return selections;
    };

    /**
     * 获取详情审计项列表
     */
    getAuditItemDetailList = (num: number): any => {
        if (num === this.AUDIT_ITEMS_ALL_VALUE) {
            return Object.values(this.auditItem).map((item) => item);
        } else if (num) {
            const selections = this.getSelectedAuditItem(num);
            return selections.map((item) => this.auditItem[item]);
        } else {
            return [];
        }
    };

    /**
     * 将内容用括号包起来
     */
    bracket = (content: string): string => this.get('common.common_unit_lab', [content]);

    /**
     * 已分配容量渲染
     */
    usedCapacityRender = (usedCapacity: string, totalCapacity: string): string => this.get('common.allocatedandTotal_tip', [
        this.utilService.adaptUnit(usedCapacity, CAPACITY_UNIT.SECTOR).label,
        this.utilService.adaptUnit(totalCapacity, CAPACITY_UNIT.SECTOR).label,
    ]);

    noPermissionToUser = (permissionList: string[]): string =>
        this.get('common.noPermissionToCreateHypermetroFS_valid', [permissionList.join(this.get('common.comma_value'))]);

    /**
     * 未使用容量渲染
     */
    freeCapacityRender = (oriFreeCapacity: string | number): string => {
        let freeCapacity = oriFreeCapacity;
        freeCapacity = Math.max(0, Number(freeCapacity));
        return this.get('common.unused_tip', [
            this.utilService.adaptUnit(freeCapacity, CAPACITY_UNIT.SECTOR).label,
        ]);
    };

    /**
     * 移除结果
     * @param name
     */
    removeResult(name: string, partition: string): string {
        const msg = this.get('rest.removeFSfromSmartCachePartition_msg', [partition, name]);
        return msg;
    }

    /**
     * 名称验证，支持多国语言文字
     */
    nameValidator = (control: AbstractControl): ValidationErrors | null => {
        const reg = /[`~!@#$%\^&\*\(\)\=\+\|\{\};:'\",<>\/\\\?\[\]\s]+/;
        const isValid = !control.value.match(reg);
        return isValid ? null : { name: true };
    };

    /**
     * path验证，只限制以‘/’开头
     */
    pathValidator = (control: AbstractControl): ValidationErrors | null => {
        const reg = /^[\/]/;
        const isValid = reg.test(control.value);
        return isValid ? null : { path: true };
    };

    /**
     * 修改审计项时值是否变化
     */
    isAuditItemsChanged = (changedValue: number, originValue: number): boolean => {
        const selectedItems = this.getSelectedAuditItem(changedValue);
        // 如果底层传上来是全选，对比所选数量和审计项数量是否一致
        if (originValue === this.AUDIT_ITEMS_ALL_VALUE) {
            return selectedItems.length !== Object.keys(this.auditItem).length;
        } else {
            return changedValue !== originValue;
        }
    };

    /**
     * worm诉讼保留任务字段
     */
    formatLitingTaskOperation = (status: number | string): string => this.wormLitingTaskOperationType[status] || UNPLACEHOLDER;

    formatLitingStatus = (status: number | string): string => this.wormLitingStatus[status] || UNPLACEHOLDER;

    formatLitingDetailStatus = (status: number | string): string => this.wormLitingDetailStatus[status] || UNPLACEHOLDER;

    formatLitingOperation = (status: number | string): string => this.wormLitingOperationType[status] || UNPLACEHOLDER;

    /**
     * 容量自适应策略选项
     */
    capacityPolicyOptions: SelectOption[] = [
        { label: this.i18n.policyDisableLab, value: CapacityPolicyE.DISABLED },
        { label: this.i18n.policyAutoExpandLab, value: CapacityPolicyE.AUTOEXPANSION },
        { label: this.i18n.policyAutoExpandorReductionLab, value: CapacityPolicyE.AUTOEXPANSIONANDREDUCTION },
    ];

    /**
     * fs CDP计划创建快照开关label
     */
    fsCdpEnableSwitchLabel = {
        true: this.get('common.enabled_switch_column'),
        false: this.get('common.service_disable_value'),
    };

    /**
     * 文件系统详情保护页签显示条件
     * @returns
     */
    showFsDetailProtectionTab(): boolean {
        return this.showSnapShotFeature() ||
            this.showCdpFeature() ||
            this.showCloneFeature() ||
            this.showReplicationFeature() ||
            this.showNasHyperMetroFeature();
    }

    showSnapShotFeature(): boolean {
        return this.permissionService.checkShow(['fs_snapshot_R'], [[LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]]);
    }

    showCdpFeature(): boolean {
        return this.permissionService.checkShow(['fs_snapshot_R', '@hyper_cdp_R'], [LicenseKey.HyperCDP]);
    }

    showCloneFeature(): boolean {
        return this.permissionService.checkShow(['file_system_R'], [LicenseKey.HyperClone]);
    }

    showReplicationFeature(): boolean {
        return this.permissionService.checkShow(['remote_replication_R'], [LicenseKey.HyperReplication]);
    }

    showNasHyperMetroFeature(): boolean {
        return FeatureConfig.IsSupportNASHyperMetro && this.permissionService.hasPermission('hyper_metro_pair_R');
    }
}
